import ArrowForward from "@mui/icons-material/ArrowForward";
import ExpandMore from "@mui/icons-material/ExpandMore";
import {
  Box,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

const CATEGORY_MENU_PATHS = {
  applications: "/govnr/applications",
  disbursements: "/govnr/disbursements",
  childTransfers: "/govnr/transfers",
  expenses: "/govnr/expenses",
};

const getDefaultExpandedTypeIds = (category) =>
  new Set(
    (category.key === "expenses"
      ? category.types.slice(0, 1)
      : category.types
    ).map((type) => type.typeId)
  );

const getStatusExplorerPath = (menuPath, typeId, statusId) => {
  const searchParams = new URLSearchParams({
    typeId: String(typeId),
    statusId: String(statusId),
  });

  return `${menuPath}?${searchParams.toString()}`;
};

const Count = ({ label, value }) => (
  <Box
    sx={{
      minWidth: 82,
      px: 0.75,
      py: 0.5,
      display: "flex",
      alignItems: "baseline",
      justifyContent: "space-between",
      gap: 0.75,
      borderRadius: 1.25,
      bgcolor: "rgba(25, 58, 122, 0.045)",
      whiteSpace: "nowrap",
    }}
  >
    <Typography
      component="span"
      variant="caption"
      color="text.secondary"
      sx={{ fontSize: "0.68rem", lineHeight: 1.15 }}
    >
      {label}
    </Typography>
    <Typography component="strong" fontWeight={700} sx={{ fontSize: "0.92rem" }}>
      {value.toLocaleString("el-GR")}
    </Typography>
  </Box>
);

const StatusTile = ({ status, to }) => (
  <Box
    component={to ? Link : "div"}
    {...(to ? { to } : {})}
    sx={{
      position: "relative",
      height: "100%",
      minHeight: 68,
      minWidth: 0,
      overflow: "hidden",
      p: 1.25,
      pl: 1.75,
      border: 1,
      borderColor: "divider",
      borderRadius: 1.25,
      bgcolor: "background.paper",
      boxShadow: "0 4px 14px rgba(30, 52, 82, 0.06)",
      color: "inherit",
      textDecoration: "none",
      cursor: to ? "pointer" : "default",
      transition: "box-shadow 150ms ease, border-color 150ms ease",
      ...(to && {
        "&:hover": {
          borderColor: "primary.light",
          boxShadow: "0 6px 18px rgba(30, 52, 82, 0.12)",
        },
        "&:focus-visible": {
          outline: "2px solid",
          outlineColor: "primary.main",
          outlineOffset: 2,
        },
      }),
    }}
  >
    <Box
      aria-hidden="true"
      className={status.statusCssClass}
      sx={{
        position: "absolute",
        inset: "0 auto 0 0",
        width: 5,
      }}
    />
    <Stack
      direction="row"
      justifyContent="space-between"
      alignItems="center"
      spacing={1}
      sx={{
        position: "relative",
        zIndex: 1,
        minWidth: 0,
        height: "100%",
      }}
    >
      <Typography
        variant="body2"
        sx={{
          minWidth: 0,
          pr: 1,
          lineHeight: 1.25,
          overflowWrap: "anywhere",
          display: "-webkit-box",
          WebkitLineClamp: 2,
          WebkitBoxOrient: "vertical",
          overflow: "hidden",
        }}
      >
        {status.statusName}
      </Typography>
      <Box
        className={status.statusCssClass}
        sx={{
          minWidth: 36,
          px: 1,
          py: 0.25,
          borderRadius: 10,
          textAlign: "center",
          fontSize: "0.75rem",
          lineHeight: 1.5,
          fontWeight: 700,
          flexShrink: 0,
        }}
      >
        {status.count.toLocaleString("el-GR")}
      </Box>
    </Stack>
  </Box>
);

const DashboardTypeList = ({ category }) => {
  const navigate = useNavigate();
  const [expandedTypeIds, setExpandedTypeIds] = useState(
    getDefaultExpandedTypeIds(category)
  );

  useEffect(() => {
    setExpandedTypeIds(getDefaultExpandedTypeIds(category));
  }, [category.key, category.types]);

  const toggleType = (typeId) => {
    setExpandedTypeIds((currentTypeIds) => {
      const nextTypeIds = new Set(currentTypeIds);

      if (nextTypeIds.has(typeId)) {
        nextTypeIds.delete(typeId);
      } else {
        nextTypeIds.add(typeId);
      }

      return nextTypeIds;
    });
  };

  const handleRowKeyDown = (event, typeId) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      toggleType(typeId);
    }
  };

  const menuPath = CATEGORY_MENU_PATHS[category.key];

  return (
    <Paper
      component="section"
      aria-labelledby="dashboard-types-title"
      variant="outlined"
      sx={{ overflow: "hidden", borderRadius: 2 }}
    >
      <Box
        sx={{
          px: 2,
          py: 1.5,
          borderBottom: 1,
          borderColor: "divider",
          backgroundColor: "#dff2ff",
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1}>

          <Box>
            <Typography
              id="dashboard-types-title"
              component="h2"
              variant="h4"
            >
              {category.title}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Ανάλυση ανά τύπο και κατάσταση
            </Typography>
          </Box>
          {menuPath && (
            <Tooltip title={`Μετάβαση στο μενού: ${category.title}`}>
              <IconButton
                size="small"
                aria-label={`Μετάβαση στο μενού: ${category.title}`}
                onClick={() => navigate(menuPath)}
                sx={{ ml: -0.75 }}
              >
                <ArrowForward />
              </IconButton>
            </Tooltip>
          )}
        </Stack>
      </Box>

      {category.types.length === 0 ? (
        <Typography color="text.secondary" sx={{ p: 3, textAlign: "center" }}>
          Δεν υπάρχουν διαθέσιμα στοιχεία τύπων.
        </Typography>
      ) : (
        <Box sx={{ p: 1.5, display: "grid", gap: 1.1 }}>
          {category.types.map((type) => {
            const expanded = expandedTypeIds.has(type.typeId);
            const visibleStatuses = type.statuses.filter(
              (status) => status.count !== 0
            );

            return (
              <Box
                key={type.typeId}
                sx={{
                  minWidth: 0,
                  border: 1,
                  borderColor: expanded
                    ? "rgba(25, 58, 122, 0.32)"
                    : "divider",
                  borderRadius: 1.25,
                  bgcolor: expanded ? "#f4f9fd" : "background.paper",
                  boxShadow: expanded
                    ? "0 10px 24px rgba(30, 52, 82, 0.12)"
                    : "0 4px 14px rgba(30, 52, 82, 0.06)",
                  transition:
                    "box-shadow 150ms ease, border-color 150ms ease, background-color 150ms ease",
                }}
              >
                <Box
                  role="button"
                  tabIndex={0}
                  aria-expanded={expanded}
                  aria-controls={`dashboard-type-${type.typeId}-content`}
                  onClick={() => toggleType(type.typeId)}
                  onKeyDown={(event) => handleRowKeyDown(event, type.typeId)}
                  sx={{
                    minWidth: 0,
                    display: "grid",
                    gridTemplateColumns: {
                      xs: "minmax(0, 1fr)",
                      sm: "minmax(0, 1fr) auto auto 28px",
                    },
                    gap: { xs: 0.75, sm: 1.25 },
                    alignItems: "center",
                    px: 1.25,
                    py: 1,
                    cursor: "pointer",
                    "&:hover": {
                      bgcolor: "#f4f9fd",
                    },
                    "&:focus-visible": {
                      outline: "2px solid rgba(25, 58, 122, 0.45)",
                      outlineOffset: -2,
                    },
                  }}
                >
                  <Typography fontWeight={700} sx={{ minWidth: 0 }}>
                    {type.typeName}
                  </Typography>
                  <Count label="Σύνολο" value={type.totalCount} />
                  <Count label="Ενεργές" value={type.activeCount} />
                  <ExpandMore
                    fontSize="small"
                    sx={{
                      color: "primary.main",
                      justifySelf: "center",
                      transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
                      transition: "transform 150ms ease",
                      display: { xs: "none", sm: "block" },
                    }}
                  />
                </Box>

                {expanded && (
                  <Box
                    id={`dashboard-type-${type.typeId}-content`}
                    sx={{
                      mx: 1.25,
                      mb: 1.25,
                      pt: 1.25,
                      borderTop: 1,
                      borderColor: "rgba(25, 58, 122, 0.12)",
                    }}
                  >
                    {visibleStatuses.length === 0 ? (
                      <Typography variant="body2" color="text.secondary">
                        Δεν υπάρχουν στοιχεία καταστάσεων.
                      </Typography>
                    ) : (
                      <Box
                        sx={{
                          display: "grid",
                          gridTemplateColumns: {
                            xs: "1fr",
                            sm: "repeat(2, minmax(0, 1fr))",
                            xl: "repeat(3, minmax(0, 1fr))",
                          },
                          gap: 1,
                          gridAutoRows: "1fr",
                        }}
                      >
                        {visibleStatuses.map((status) => (
                          <StatusTile
                            key={status.statusId}
                            status={status}
                            to={
                              category.key === "expenses"
                                ? undefined
                                : getStatusExplorerPath(
                                    menuPath,
                                    type.typeId,
                                    status.statusId
                                  )
                            }
                          />
                        ))}
                      </Box>
                    )}
                  </Box>
                )}
              </Box>
            );
          })}
        </Box>
      )}
    </Paper>
  );
};

export default DashboardTypeList;

